import { ProductCategory } from "./types";

export interface PrepPackFormValues {
    brand_name: string;
    product_urls: string[];
    category: string;
    lead_source: string;
    meeting_type: string;
    meeting_goal: string;
    decision_maker: string;
    primary_brand: string;
    secondary_brand: string;
    key_attributes: string;
    adjacent_brands: string;
    subreddits: string;
    pdp_findings: string;
    notes: string;
}

export const INITIAL_PREP_PACK_VALUES: PrepPackFormValues = {
    brand_name: "",
    product_urls: [""],
    category: "",
    lead_source: "Not specified",
    meeting_type: "Not specified",
    meeting_goal: "Not specified",
    decision_maker: "",
    primary_brand: "",
    secondary_brand: "",
    key_attributes: "",
    adjacent_brands: "",
    subreddits: "",
    pdp_findings: "",
    notes: "",
};

export const LEAD_SOURCES = [
    "Not specified",
    "PDP audit",
    "Referral",
    "Landing page",
    "Cold outreach",
    "Event",
    "Inbound",
    "Other",
];

export const MEETING_TYPES = [
    "Not specified",
    "Discovery",
    "Demo",
    "Pitch",
    "Renewal",
    "Follow-up",
];

export const MEETING_GOALS = [
    "Not specified",
    "Book a pilot",
    "Confirm decision process",
    "Close the deal",
    "Build relationship",
    "Upsell / expand",
];

export const WIZARD_STEPS = [
    { key: "prospect", label: "Prospect", required: true },
    { key: "context", label: "Context", required: true },
    { key: "detail", label: "Detail", required: false },
    { key: "review", label: "Review", required: false },
];

// The real pipeline stages (the server reports live progress via
// `pack.stage`; see STAGE_TO_STEP below). Used as tooltip copy on the
// list's Generating badge.
export const GENERATING_STEPS = [
    "Auditing product pages (scores & gaps)",
    "Finding the communities & search terms shoppers use",
    "Researching company & category (live web) + Consumer Voice analysis",
    "Fact-checking sources & gathering real customer voice",
    "Assembling the structured pack",
    "Curating TDC experts",
    "Running the QA gate",
    "Finalizing the prep pack",
];

// Server `stage` value -> GENERATING_STEPS index, so the list badge
// tracks actual pipeline position instead of a timer guess.
export const STAGE_TO_STEP: Record<string, number> = {
    audits: 0,
    discovery: 1,
    research: 2,
    fact_check_voc: 3,
    assemble: 4,
    expert_curation: 5,
    qa: 6,
    done: 7,
};

// Compact stage names shown inline next to "Generating" in the list
// (the full GENERATING_STEPS strings stay in the tooltip).
export const STAGE_SHORT_LABELS: Record<string, string> = {
    audits: "auditing pages",
    discovery: "finding communities",
    research: "researching",
    fact_check_voc: "fact-checking",
    assemble: "assembling",
    expert_curation: "curating experts",
    qa: "QA gate",
    done: "finalizing",
};

// Section titles shared by the v1 TOC and the v2 doc (the v2 Source Index
// is code-built server-side; "Recommended Experts" renders from
// report.experts).
export const PREP_PACK_TOC = [
    "1. Executive Summary",
    "2. Company and Brand Profile",
    "3. Decision-Maker Intelligence",
    "4. Category Landscape",
    "5. Voice of Customer",
    "6. Meeting Game Plan",
    "7. TDC Package Recommendation",
    "8. Source Index",
];

export interface CategoryOption {
    id: number;
    name: string;
    path: string;
    l1: string;
    depth: number;
    subPath: string;
}

export const catLabel = (o: CategoryOption | string) =>
    typeof o === "string" ? o : o.name;

export const subLabel = (o: CategoryOption | string) =>
    (typeof o === "string" ? o : o.subPath || o.name).trim();

export const buildCategoryOptions = (
    list: ProductCategory[],
): CategoryOption[] => {
    const byId = new Map(list.map((c) => [c.id, c]));
    const partsOf = (cat: ProductCategory) => {
        const parts: string[] = [];
        let cur: ProductCategory | undefined = cat;
        let guard = 0;
        while (cur && guard++ < 10) {
            parts.unshift(cur.name);
            cur = cur.parent_id != null ? byId.get(cur.parent_id) : undefined;
        }
        return parts;
    };
    return list
        .filter((c) => c && c.name)
        .map((c) => {
            const parts = partsOf(c);
            return {
                id: c.id,
                name: c.name,
                path: parts.join(" › "),
                l1: parts[0] || c.name,
                depth: parts.length,
                subPath: parts.slice(1).join(" › "),
            };
        })
        .sort((a, b) => a.path.localeCompare(b.path));
};

export const summariseSubs = (subs: (CategoryOption | string)[]) => {
    const order: string[] = [];
    const leavesByParent = new Map<string, string[]>();

    subs.map(subLabel)
        .filter(Boolean)
        .forEach((label) => {
            const cut = label.lastIndexOf("›");
            const parent = cut === -1 ? "" : label.slice(0, cut).trim();
            const leaf = cut === -1 ? label : label.slice(cut + 1).trim();
            if (!leavesByParent.has(parent)) {
                leavesByParent.set(parent, []);
                order.push(parent);
            }
            const leaves = leavesByParent.get(parent) as string[];
            if (!leaves.includes(leaf)) leaves.push(leaf);
        });

    return order
        .map((parent) => {
            const leaves = (leavesByParent.get(parent) as string[]).join(", ");
            return parent ? `${parent} › ${leaves}` : leaves;
        })
        .join("; ");
};

export const composeCategory = (
    l1: string,
    subs: (CategoryOption | string)[],
) => {
    const broad = (l1 || "").trim();
    const detail = summariseSubs(subs);
    if (!broad) return detail;
    return detail ? `${broad}: ${detail}` : broad;
};
